import api from '@shared/services/api';
import { QuestionBankTest, QuestionBankTestAnswer } from '../types/question-bank.types';

/**
 * Service for handling question bank test API calls
 */
class QuestionBankTestService {
  private readonly BASE_URL = `/question-bank/tests`;

  /**
   * Fetch all tests for the current user
   * @returns Promise with tests data
   */
  async getUserTests(): Promise<QuestionBankTest[]> {
    try {
      const response = await api.get(this.BASE_URL);
      return response.data.tests || [];
    } catch (error) {
      console.error('Error fetching tests:', error);
      throw error;
    }
  }

  /**
   * Fetch a single test by ID
   * @param testId Test ID
   * @returns Promise with test data
   */
  async getTestById(testId: string): Promise<QuestionBankTest> {
    try {
      const response = await api.get(`${this.BASE_URL}/${testId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching test:', error);
      throw error;
    }
  }

  /**
   * Generate a new test
   * @param categoryId Category ID
   * @param questionCount Number of questions
   * @param name Test name
   * @returns Promise with generated test
   */
  async generateTest(
    categoryId: string,
    questionCount: number,
    name: string
  ): Promise<QuestionBankTest> {
    try {
      const response = await api.post(this.BASE_URL, {
        categoryId,
        questionCount,
        name,
      });
      return response.data.test;
    } catch (error) {
      console.error('Error generating test:', error);
      throw error;
    }
  }

  /**
   * Save an answer for a test
   * @param testId Test ID
   * @param questionId Question ID
   * @param selectedOption Selected option
   * @returns Promise with updated progress
   */
  async saveAnswer(
    testId: string,
    questionId: string,
    selectedOption: string
  ): Promise<any> {
    try {
      const response = await api.post(`${this.BASE_URL}/${testId}/answers`, {
        questionId,
        selectedOption,
      });
      return response.data.progress;
    } catch (error) {
      console.error('Error saving answer:', error);
      throw error;
    }
  }

  /**
   * Complete a test
   * @param testId Test ID
   * @returns Promise with test results
   */
  async completeTest(testId: string): Promise<QuestionBankTest> {
    try {
      const response = await api.post(`${this.BASE_URL}/${testId}/complete`);
      return response.data;
    } catch (error) {
      console.error('Error completing test:', error);
      throw error;
    }
  }

  /**
   * Restart a test
   * @param testId Test ID
   * @returns Promise with restarted test
   */
  async restartTest(testId: string): Promise<QuestionBankTest> {
    try {
      const response = await api.post(`${this.BASE_URL}/${testId}/restart`);
      return response.data.test;
    } catch (error) {
      console.error('Error restarting test:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const questionBankTestService = new QuestionBankTestService();
